import { Anchor, Group, Paper, Text, Tooltip } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { TeamDailyBoard, TeamDailyMember, TeamDailyTicket } from "@msc/shared";

import { stunden } from "./prioritaet.js";
import { formatIsoDate } from "../../lib/date.js";
import { anzeigeName } from "../../lib/anzeigeName.js";
import { buchungsstand } from "../../lib/buchungsZeichen.js";
import { TicketRow } from "./TicketRow.js";

/**
 * Wie viele Tickets je Person sofort stehen. Zehn — darunter fängt das
 * Aufklappen an, und wer zehn offene Vorgänge hat, soll sie im Daily sehen,
 * ohne zu klicken.
 */
const TICKET_CAP = 10;

/**
 * Die Ampel misst **Auslastung, nicht Überlastung**: Grün heißt „hat genug zu
 * tun", Rot heißt „hat zu wenig".
 *
 * Das ist die Frage eines Servicebereichs — leerlaufende Stunden sind das
 * Problem, nicht ein voller Rücken. Die erste Fassung war andersherum gebaut
 * (rot ab 100 %) und hätte gerade den gut ausgelasteten Tag als Alarm gezeigt.
 *
 * Ab 100 % der verfügbaren Stunden ist der Vorrat gedeckt (grün), ab 60 %
 * reicht er knapp (orange), darunter fehlt Arbeit (rot).
 */
const AMPEL_GRUEN = 1.0;
const AMPEL_ORANGE = 0.6;

/**
 * Das Board als **dichtes, umbrechendes Raster**: je Teammitglied eine kompakte
 * Karte, die sich der Breite anpasst und umbricht — so ist das ganze Team auf
 * einen Blick da, ohne Seitwärts-Scrollen. Read-only (kein Ziehen).
 *
 * Unter dem Namen steht der **Backlog gegen die Kapazität**: aktiver
 * Restaufwand, was davon beim Kunden wartet, und wie viele Stunden diese Woche
 * überhaupt zur Verfügung stehen. Erst dieses Verhältnis beantwortet die Frage,
 * um die es im Daily geht — wer hat noch Luft.
 */
export function BoardColumns({ board }: { board: TeamDailyBoard }) {
  const { t } = useTranslation();

  const byAccount = new Map<string, TeamDailyMember>();
  for (const m of board.members) {
    if (m.jiraAccountId) byAccount.set(m.jiraAccountId, m);
  }

  const ticketsByEmployee = new Map<string, TeamDailyTicket[]>();
  const orphans: TeamDailyTicket[] = [];
  for (const ticket of board.tickets) {
    const member = ticket.assigneeAccountId
      ? byAccount.get(ticket.assigneeAccountId)
      : undefined;
    if (!member) {
      orphans.push(ticket);
      continue;
    }
    const list = ticketsByEmployee.get(member.employeeId) ?? [];
    list.push(ticket);
    ticketsByEmployee.set(member.employeeId, list);
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
        gap: 8,
        alignItems: "start",
      }}
    >
      {board.members.map((member) => (
        <MemberCard
          key={member.employeeId}
          title={anzeigeName(member.fullName, board.date)}
          member={member}
          tickets={ticketsByEmployee.get(member.employeeId) ?? []}
          jiraBaseUrl={board.jiraBaseUrl}
          jiraLive={board.jiraLive}
          bookedOnDate={board.bookedOnDate}
        />
      ))}
      {orphans.length > 0 && (
        <MemberCard
          title={t("pages.teamDaily.otherTickets")}
          tickets={orphans}
          jiraBaseUrl={board.jiraBaseUrl}
          jiraLive={board.jiraLive}
          bookedOnDate={board.bookedOnDate}
        />
      )}
    </div>
  );
}

function MemberCard({
  title,
  tickets,
  member,
  jiraBaseUrl,
  jiraLive,
  bookedOnDate,
}: {
  title: string;
  tickets: TeamDailyTicket[];
  member?: TeamDailyMember;
  jiraBaseUrl: string | null;
  jiraLive: boolean;
  bookedOnDate: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const shown = expanded ? tickets : tickets.slice(0, TICKET_CAP);
  const hidden = tickets.length - shown.length;
  const absent = member?.absence != null;

  // Wartende Vorgänge liefert der Server gar nicht mehr — was hier steht, ist
  // die Arbeit, die heute bei dieser Person liegt.
  const aktivSekunden = tickets.reduce((s, x) => s + x.remainingSeconds, 0);

  const kapazitaet = member?.capacityHours ?? 0;
  const auslastung = kapazitaet > 0 ? aktivSekunden / 3600 / kapazitaet : null;
  const ampel =
    auslastung === null
      ? "dimmed"
      : auslastung >= AMPEL_GRUEN
        ? "green.7"
        : auslastung >= AMPEL_ORANGE
          ? "orange.7"
          : "red.7";

  return (
    <Paper
      withBorder
      p={8}
      style={{
        borderTop: `2px solid var(--mantine-color-${absent ? "gray" : "orange"}-5)`,
        opacity: absent ? 0.85 : 1,
      }}
    >
      <Group justify="space-between" gap={4} wrap="nowrap">
        <Text size="sm" fw={600} truncate>
          {title}
        </Text>
        <Group gap={5} wrap="nowrap" style={{ flexShrink: 0 }}>
          {/* Drei Zustände, nicht zwei: gebucht, nicht gebucht, unbekannt.
              Vorher stand bei „nicht gebucht" nichts — und damit dasselbe wie
              bei jemandem ohne auflösbares Jira-Konto (s. `buchungsZeichen`). */}
          {member &&
            (() => {
              const stand = buchungsstand(member, jiraLive);
              const zeichen =
                stand === "gebucht" ? "✓" : stand === "offen" ? "–" : "?";
              const farbe =
                stand === "gebucht"
                  ? "green.7"
                  : stand === "offen"
                    ? "red.7"
                    : "dimmed";
              const beschriftung = t(`pages.teamDaily.booking.${stand}`, {
                datum: formatIsoDate(bookedOnDate),
              });
              return (
                <Text
                  size="xs"
                  fw={700}
                  c={farbe}
                  title={beschriftung}
                  aria-label={beschriftung}
                >
                  {zeichen}
                </Text>
              );
            })()}
          {member?.absence && (
            <Text size="xs" c="dimmed">
              {member.absence.dayFraction >= 1
                ? t("pages.teamDaily.absent")
                : t("pages.teamDaily.absentPartial")}
            </Text>
          )}
        </Group>
      </Group>

      {/* Backlog gegen Kapazität — die eigentliche Aussage der Karte. */}
      {tickets.length === 0 ? (
        <Text size="xs" c="dimmed" mb={2}>
          {t("pages.teamDaily.noTickets")}
        </Text>
      ) : (
        <Tooltip
          label={
            member
              ? t("pages.teamDaily.capacityHint", {
                  hours: stunden(aktivSekunden),
                  capacity: kapazitaet,
                })
              : t("pages.teamDaily.backlogPlain")
          }
          multiline
          w={240}
        >
          <Text size="xs" c={ampel} fw={500} mb={2}>
            {member
              ? t("pages.teamDaily.capacityLine", {
                  hours: stunden(aktivSekunden),
                  capacity: kapazitaet,
                  count: tickets.length,
                })
              : t("pages.teamDaily.backlogLine", {
                  hours: stunden(aktivSekunden),
                  count: tickets.length,
                })}
          </Text>
        </Tooltip>
      )}

      {shown.map((ticket) => (
        <TicketRow key={ticket.key} ticket={ticket} jiraBaseUrl={jiraBaseUrl} />
      ))}
      {hidden > 0 && (
        <Anchor
          component="button"
          type="button"
          size="xs"
          onClick={() => setExpanded(true)}
          mt={4}
        >
          {t("pages.teamDaily.moreTickets", { count: hidden })}
        </Anchor>
      )}
    </Paper>
  );
}
